/**
 * The small set of components every screen is built from.
 *
 * Deliberately small, and deliberately not a component library. Each one exists
 * because the same arrangement appears on three screens and the third copy would
 * have drifted — and each stops at the point where a screen would need to fight it.
 * Anything used once lives in the screen that uses it.
 */
import { useEffect, useRef, useState, type ReactNode } from "react";

/* --------------------------------------------------------------------- icons */

/**
 * The icons, inline.
 *
 * Twelve of them, drawn as paths, rather than a font or an icon package. A font
 * costs a network round trip before the first screen paints and a package costs a
 * dependency for twelve shapes; both are the wrong trade at this size. Stroked
 * rather than filled, at 1.6, which is what makes them sit beside IBM Plex without
 * shouting.
 */
const PATHS: Record<string, string> = {
  upload: "M12 16V4m0 0L7 9m5-5 5 5M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2",
  data: "M4 6c0-1.1 3.6-2 8-2s8 .9 8 2-3.6 2-8 2-8-.9-8-2Zm0 0v12c0 1.1 3.6 2 8 2s8-.9 8-2V6M4 12c0 1.1 3.6 2 8 2s8-.9 8-2",
  design: "M4 20h16M6 16l3-8 3 8m-5-3h4m6 3V8m0 0-2 2m2-2 2 2",
  document: "M14 3v5h5M6 3h8l5 5v13H6V3Zm3 11h7m-7 4h7",
  plus: "M12 5v14M5 12h14",
  check: "m4 13 5 5L20 6",
  chevron: "m9 6 6 6-6 6",
  back: "m14 6-6 6 6 6",
  download: "M12 4v12m0 0 5-5m-5 5-5-5M4 19h16",
  warn: "M12 9v5m0 3h.01M10.3 4.6 2.9 17.4A1.9 1.9 0 0 0 4.6 20h14.8a1.9 1.9 0 0 0 1.7-2.6L13.7 4.6a1.9 1.9 0 0 0-3.4 0Z",
  info: "M12 11v6m0-9h.01M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Z",
  cross: "M6 6l12 12M18 6 6 18",
  spark: "M12 3v3m0 12v3M3 12h3m12 0h3M5.6 5.6l2.1 2.1m8.6 8.6 2.1 2.1m0-12.8-2.1 2.1M7.7 16.3l-2.1 2.1",
  trash: "M4 7h16M9 7V4h6v3m-8 0 1 13h8l1-13",
  more: "M6 12h.01M12 12h.01M18 12h.01",
  /** Three dots stacked, drawn with a heavy stroke so they read as dots at 16px. */
  options: "M12 5.5h.01M12 12h.01M12 18.5h.01",
  folder: "M4 7a1 1 0 0 1 1-1h4l2 2h8a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7Z",
  pencil: "M4 20h4L20 8l-4-4L4 16v4Z",
  home: "M4 11 12 4l8 7v9H4v-9Z",
  share: "M9 15 15 9M10.5 6.5l1.8-1.8a4 4 0 0 1 5.7 5.7l-1.8 1.8m-4.7 4.7-1.8 1.8a4 4 0 0 1-5.7-5.7l1.8-1.8",
  lock: "M7 11V8a5 5 0 0 1 10 0v3M6 11h12v9H6v-9Z",
  history: "M4 12a8 8 0 1 0 2.4-5.7M4 4v4h4m4-1v5l3 2",
  eye: "M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Zm9.5 3a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z",
  external: "M14 4h6v6m0-6-9 9m7 1v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5",
  copy: "M9 9h11v11H9V9Zm-5 6V4h11",
  /** A file kept in somebody's cloud storage. Generic on purpose: the services' own marks are theirs. */
  cloud: "M7 18.5h10.5a4 4 0 0 0 .5-7.97A6 6 0 0 0 6.3 9.2 4.7 4.7 0 0 0 7 18.5Z",
  refresh: "M19.5 12a7.5 7.5 0 0 1-13.4 4.6M4.5 12a7.5 7.5 0 0 1 13.4-4.6M18 3.5v4h-4M6 20.5v-4h4",
  people: "M3.5 19v-1a4 4 0 0 1 4-4h3a4 4 0 0 1 4 4v1M9 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Zm8.5 3.2a4 4 0 0 1 3 3.8v1M15.5 4.3a3.5 3.5 0 0 1 0 6.4",
  help: "M9.5 9a2.5 2.5 0 1 1 3.5 2.3c-.6.3-1 .9-1 1.6V14m0 3h.01M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Z",
  settings: "M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm7.4-3a7.4 7.4 0 0 0-.1-1.2l2-1.5-2-3.4-2.3 1a7.5 7.5 0 0 0-2-1.2L14.6 3H9.4l-.4 2.5a7.5 7.5 0 0 0-2 1.2l-2.3-1-2 3.4 2 1.5a7.4 7.4 0 0 0 0 2.4l-2 1.5 2 3.4 2.3-1a7.5 7.5 0 0 0 2 1.2l.4 2.5h5.2l.4-2.5a7.5 7.5 0 0 0 2-1.2l2.3 1 2-3.4-2-1.5c.07-.4.1-.8.1-1.2Z"
};

export function Icon({ name, className = "btn__icon" }: { name: keyof typeof PATHS | string; className?: string }) {
  const d = PATHS[name] ?? PATHS.info!;
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={d} />
    </svg>
  );
}

/* ------------------------------------------------------------------- buttons */

export function Button({
  children,
  onClick,
  variant = "default",
  size,
  icon,
  disabled,
  busy,
  type = "button",
  block,
  title
}: {
  children?: ReactNode;
  onClick?: () => void;
  variant?: "default" | "primary" | "ink" | "quiet" | "danger";
  size?: "sm" | "lg";
  icon?: string;
  disabled?: boolean;
  /** Shows a spinner and refuses further clicks. */
  busy?: boolean;
  type?: "button" | "submit";
  block?: boolean;
  title?: string;
}) {
  const classes = ["btn", variant !== "default" && `btn--${variant}`, size && `btn--${size}`, block && "btn--block"].filter(Boolean).join(" ");
  return (
    <button type={type} className={classes} onClick={onClick} disabled={disabled || busy} {...(title ? { title } : {})}>
      {busy ? <span className="spinner" /> : icon ? <Icon name={icon} /> : null}
      {children}
    </button>
  );
}

/* --------------------------------------------------------------------- panes */

export function Card({ children, className = "", flat }: { children: ReactNode; className?: string; flat?: boolean }) {
  return <div className={`card${flat ? " card--flat" : ""} ${className}`.trim()}>{children}</div>;
}

export function Field({ label, hint, error, children, id }: { label: string; hint?: string; error?: string; children: ReactNode; id?: string }) {
  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
      </label>
      {children}
      {error ? <span className="field__error">{error}</span> : hint ? <span className="field__hint">{hint}</span> : null}
    </div>
  );
}

/** A short line of guidance, reassurance or warning. Never a stack trace. */
export function Note({ tone = "info", title, children }: { tone?: "info" | "ask" | "bad" | "good"; title?: string; children: ReactNode }) {
  const icon = tone === "bad" || tone === "ask" ? "warn" : tone === "good" ? "check" : "info";
  return (
    <div className={`note note--${tone}`}>
      <Icon name={icon} className="note__icon" />
      <div>
        {title ? <h3>{title}</h3> : null}
        {/* A div, not a paragraph: several notes hold a list, and a list inside a
            paragraph is invalid HTML that React warns about on every render. */}
        <div className="note__body">{children}</div>
      </div>
    </div>
  );
}

export function Chip({ tone, children }: { tone?: "good" | "ask" | "bad" | "info"; children: ReactNode }) {
  return <span className={`chip${tone ? ` chip--${tone}` : ""}`}>{children}</span>;
}

/**
 * The empty state, which on several screens is the primary interface.
 *
 * It takes actions rather than just describing an absence, because the brief is
 * explicit: "the UI should always make the next useful action obvious", and a
 * screen that says "no datasets" and stops has failed that on sight.
 */
export function Empty({ title, children, actions }: { title: string; children?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="empty">
      <span className="empty__mark" />
      <h2>{title}</h2>
      {children ? <p>{children}</p> : null}
      {actions ? <div className="empty__actions">{actions}</div> : null}
    </div>
  );
}

/** Shaped like the thing it is standing in for, so a loading list looks like a list. */
export function Skeleton({ rows = 3, height = 64 }: { rows?: number; height?: number }) {
  return (
    <div className="grid" aria-hidden="true">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="skeleton" style={{ height }} />
      ))}
    </div>
  );
}

export function Bar({ used, total }: { used: number; total: number }) {
  const share = total <= 0 ? 0 : Math.min(1, used / total);
  const tone = share >= 1 ? " bar__fill--full" : share >= 0.8 ? " bar__fill--near" : "";
  return (
    <div className="bar">
      <div className={`bar__fill${tone}`} style={{ width: `${Math.max(share * 100, used > 0 ? 3 : 0)}%` }} />
    </div>
  );
}

/* -------------------------------------------------------------------- toasts */

export interface Toast {
  id: number;
  message: string;
  tone: "info" | "good" | "bad";
}

let nextToast = 1;
const listeners = new Set<(toasts: Toast[]) => void>();
let current: Toast[] = [];

/**
 * A message, briefly.
 *
 * Outside React on purpose: it is called from `catch` blocks, from an upload
 * handler and from a save that finished after its screen unmounted, and a hook
 * would have to be threaded through every one of them. Bad news stays twice as
 * long, because it is twice as likely to be worth reading.
 */
export function toast(message: string, tone: Toast["tone"] = "info"): void {
  const entry = { id: nextToast++, message, tone };
  current = [...current, entry];
  for (const l of listeners) l(current);
  setTimeout(() => dismiss(entry.id), tone === "bad" ? 9000 : 4500);
}

export function dismiss(id: number): void {
  current = current.filter((t) => t.id !== id);
  for (const l of listeners) l(current);
}

export function Toasts() {
  const [toasts, setToasts] = useState<Toast[]>(current);
  useEffect(() => {
    listeners.add(setToasts);
    return () => void listeners.delete(setToasts);
  }, []);
  return (
    <div className="toasts" role="status" aria-live="polite">
      {toasts.map((t) => (
        <div key={t.id} className={`toast${t.tone === "bad" ? " toast--bad" : t.tone === "good" ? " toast--good" : ""}`}>
          <span>{t.message}</span>
          <button type="button" className="toast__close" onClick={() => dismiss(t.id)} aria-label="Dismiss">
            ✕
          </button>
        </div>
      ))}
    </div>
  );
}

/* -------------------------------------------------------------------- sheets */

/**
 * The one modal in the product: confirming something that cannot be undone.
 *
 * Focus moves into it and Escape closes it, because a dialog that traps neither is
 * worse than no dialog. The destructive button is not the default one, so Enter
 * does not delete anything.
 */
/**
 * A small menu behind a button.
 *
 * Hand-rolled, and forty lines, because what the sidebar needs from a menu is a
 * list of actions that closes when you click away or press Escape. Floating UI is
 * already in the designer and belongs there — it earns its weight positioning a
 * field picker against a resizable panel — but an item's "…" in a fixed-width
 * sidebar has one sensible place to open, and pulling the dependency in for that
 * would put a focus manager and a positioning engine in the first chunk the
 * browser downloads.
 */
export function Menu({ label, children, align = "right" }: { label?: string; children: (close: () => void) => ReactNode; align?: "left" | "right" }) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false);
    };
    const key = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    // `pointerdown` rather than `click`: a menu that closes on click-up dismisses
    // itself on the same gesture that opened something underneath it.
    document.addEventListener("pointerdown", away);
    document.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("pointerdown", away);
      document.removeEventListener("keydown", key);
    };
  }, [open]);

  return (
    <div className="menu" ref={box}>
      <button
        type="button"
        className="menu__trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label ?? "More"}
        title={label ?? "More"}
        onClick={(e) => {
          // The row underneath is a link; this button is not part of following it.
          e.preventDefault();
          e.stopPropagation();
          setOpen(!open);
        }}
      >
        <Icon name="options" className="" />
      </button>
      {open ? (
        <div className={`menu__list menu__list--${align}`} role="menu">
          {children(() => setOpen(false))}
        </div>
      ) : null}
    </div>
  );
}

/** One action in a {@link Menu}. */
export function MenuItem({ children, onClick, tone }: { children: ReactNode; onClick: () => void; tone?: "bad" }) {
  return (
    <button type="button" role="menuitem" className={`menu__item${tone === "bad" ? " menu__item--bad" : ""}`} onClick={onClick}>
      {children}
    </button>
  );
}

/**
 * One line of text, asked for.
 *
 * Rename is the only thing in the product that wants it, and a browser `prompt()`
 * cannot be styled, cannot be tested and is blocked outright in some embedded
 * browsers.
 */
export function Prompt({
  title,
  label,
  initial,
  confirmLabel = "Save",
  onConfirm,
  onCancel
}: {
  title: string;
  label: string;
  initial: string;
  confirmLabel?: string;
  onConfirm: (value: string) => void;
  onCancel: () => void;
}) {
  const [value, setValue] = useState(initial);
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => {
    input.current?.focus();
    input.current?.select();
  }, []);

  return (
    <div className="scrim" onPointerDown={(e) => e.target === e.currentTarget && onCancel()}>
      <form
        className="sheet"
        onSubmit={(e) => {
          e.preventDefault();
          if (value.trim()) onConfirm(value.trim());
        }}
      >
        <div className="card__body stack" style={{ gap: 10 }}>
          <h3>{title}</h3>
          <Field label={label} id="prompt-value">
            <input
              id="prompt-value"
              ref={input}
              className="input"
              value={value}
              maxLength={200}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => e.key === "Escape" && onCancel()}
            />
          </Field>
        </div>
        <div className="card__foot row" style={{ gap: 8, justifyContent: "flex-end" }}>
          <button type="button" className="btn" onClick={onCancel}>
            Cancel
          </button>
          <Button variant="primary" type="submit" disabled={!value.trim()}>
            {confirmLabel}
          </Button>
        </div>
      </form>
    </div>
  );
}

/**
 * A dialog with room in it.
 *
 * `Confirm` asks one yes-or-no question in 460 pixels. Sharing a document and
 * deleting an account are not one question: each is a short form, with things to
 * read before anything is pressed, and cramming either into a confirmation is how a
 * person ends up agreeing to something they did not read. Escape and a click on the
 * scrim close it, as everywhere else.
 */
export function Sheet({
  title,
  children,
  footer,
  onClose,
  wide
}: {
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  onClose: () => void;
  wide?: boolean;
}) {
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    box.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="scrim" onPointerDown={(e) => e.target === e.currentTarget && onClose()}>
      <div ref={box} tabIndex={-1} className={`sheet sheet--tall${wide ? " sheet--wide" : ""}`} role="dialog" aria-modal="true" aria-label={title}>
        <div className="sheet__head">
          <h3>{title}</h3>
          <button type="button" className="sheet__close" onClick={onClose} aria-label="Close">
            <Icon name="cross" className="" />
          </button>
        </div>
        <div className="sheet__body">{children}</div>
        {footer ? <div className="card__foot row" style={{ gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>{footer}</div> : null}
      </div>
    </div>
  );
}

export function Confirm({
  title,
  children,
  confirmLabel,
  onConfirm,
  onCancel,
  busy
}: {
  title: string;
  children: ReactNode;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  busy?: boolean;
}) {
  const ref = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    ref.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  return (
    <div
      className="scrim"
      onPointerDown={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div className="sheet" role="dialog" aria-modal="true" aria-label={title}>
        <div className="card__body stack" style={{ gap: 10 }}>
          <h3>{title}</h3>
          <p className="muted">{children}</p>
        </div>
        <div className="card__foot row" style={{ gap: 8, justifyContent: "flex-end" }}>
          {/* Cancel holds the focus, so Enter is always the safe answer. */}
          <button ref={ref} type="button" className="btn" onClick={onCancel}>
            Keep it
          </button>
          <Button variant="danger" onClick={onConfirm} busy={busy}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------- format */

/** `25 MB`, `1.4 GB`. Never bytes at a person. */
export function bytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 ** 2) return `${Math.round(n / 1024)} KB`;
  if (n < 1024 ** 3) return `${(n / 1024 ** 2).toFixed(n < 10 * 1024 ** 2 ? 1 : 0)} MB`;
  return `${(n / 1024 ** 3).toFixed(1)} GB`;
}

/** `12 September 2026`, and `Today` when it is. */
export function when(iso: string | null | undefined): string {
  if (!iso) return "";
  const date = new Date(iso);
  const today = new Date();
  const sameDay = date.toDateString() === today.toDateString();
  if (sameDay) return `Today at ${date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`;
  const yesterday = new Date(today.getTime() - 86_400_000);
  if (date.toDateString() === yesterday.toDateString()) return `Yesterday at ${date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`;
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: date.getFullYear() === today.getFullYear() ? undefined : "numeric" });
}

/** `1,204`. */
export function count(n: number): string {
  return n.toLocaleString("en-GB");
}

/** `$59`, or `$19.99` when there are cents. Creem's amounts are in minor units; the site writes prices the same way. */
export function money(m: { amount: number; currency: string }): string {
  const digits = m.amount % 100 === 0 ? 0 : 2;
  return new Intl.NumberFormat("en-US", { style: "currency", currency: m.currency, minimumFractionDigits: digits, maximumFractionDigits: digits }).format(m.amount / 100);
}

/** `3 minutes ago`, `yesterday`, then a date. For activity, where "when" is a feeling rather than a timestamp. */
export function ago(iso: string | null | undefined, now = new Date()): string {
  if (!iso) return "";
  const seconds = Math.round((now.getTime() - new Date(iso).getTime()) / 1000);
  if (seconds < 45) return "just now";
  if (seconds < 3600) {
    const m = Math.max(1, Math.round(seconds / 60));
    return `${m} minute${m === 1 ? "" : "s"} ago`;
  }
  if (seconds < 86_400) {
    const h = Math.round(seconds / 3600);
    return `${h} hour${h === 1 ? "" : "s"} ago`;
  }
  return when(iso);
}

/** `ago` or `when` inside a sentence: "refreshed yesterday at 09:10", not "refreshed Yesterday at 09:10". */
export function lowerDay(text: string): string {
  return text.replace(/^(Today|Yesterday)\b/, (word) => word.toLowerCase());
}

/** `12 September at 14:20`, for a moment somebody might need to find again. */
export function at(iso: string | null | undefined): string {
  if (!iso) return "";
  const date = new Date(iso);
  const time = date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  const day = when(iso);
  return day.startsWith("Today") || day.startsWith("Yesterday") ? day : `${day} at ${time}`;
}

/* ---------------------------------------------------------------- thumbnails */

/**
 * A design's first page, or a sheet of paper waiting for one.
 *
 * The picture is drawn by the service a little while after a design changes, so a
 * brand-new design has none for its first few seconds. What stands in is a blank
 * page rather than a spinner: nothing is loading as far as the person is concerned,
 * and a grid of spinners reads as a broken screen.
 */
export function Thumb({ thumbnail, label, size = "md" }: { thumbnail: { url: string } | null; label: string; size?: "sm" | "md" }) {
  const [failed, setFailed] = useState(false);
  return (
    <div className={`thumb thumb--${size}`} aria-hidden={thumbnail && !failed ? undefined : true}>
      {thumbnail && !failed ? (
        <img src={thumbnail.url} alt={`The first page of “${label}”`} loading="lazy" decoding="async" onError={() => setFailed(true)} />
      ) : (
        <span className="thumb__blank" />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ sketches */

/**
 * A starter's page, sketched.
 *
 * The marks come from the API, which reads them from the same file the marketing
 * site's gallery draws from — so the picture in the product and the picture on the
 * home page cannot show different documents.
 *
 * Drawn rather than screenshotted for the reason the site's own comment gives: a
 * thumbnail small enough to fit six across is unreadable, and what somebody
 * actually takes from one at that size is the *shape* — where the total sits,
 * whether there is a table, whether it runs to more than a page.
 */
export type Mark =
  | { t: "rule"; y: number }
  | { t: "band"; y: number; h: number; w?: number; x?: number; tone?: "ink" | "red" | "pale" }
  | { t: "rows"; y: number; n: number }
  | { t: "chart"; y: number; h: number }
  | { t: "seal"; y: number };

const TONE = { ink: "var(--ink-3)", red: "var(--red)", pale: "var(--paper-3)" } as const;

export function Sketch({ marks }: { marks: Mark[] }) {
  return (
    <div
      style={{
        background: "var(--white)",
        border: "1px solid var(--rule)",
        borderRadius: 3,
        padding: 8,
        aspectRatio: "100 / 141",
        boxShadow: "0 1px 0 rgba(25,23,18,0.04), 0 8px 18px -14px rgba(25,23,18,0.35)"
      }}
    >
      <svg viewBox="0 0 100 140" style={{ width: "100%", height: "100%" }} aria-hidden="true">
        {marks.map((m, i) =>
          m.t === "rule" ? (
            <line key={i} x1="0" y1={m.y} x2="100" y2={m.y} stroke="var(--paper-3)" strokeWidth="0.7" />
          ) : m.t === "band" ? (
            <rect key={i} x={m.x ?? 0} y={m.y} width={m.w ?? 100} height={m.h} fill={TONE[m.tone ?? "ink"]} rx="0.5" />
          ) : m.t === "rows" ? (
            <g key={i}>
              {Array.from({ length: m.n }).map((_, r) => (
                <g key={r}>
                  <rect x="0" y={m.y + r * 8} width="46" height="2.6" fill="var(--paper-3)" rx="0.5" />
                  <rect x="62" y={m.y + r * 8} width="16" height="2.6" fill="var(--paper-3)" rx="0.5" />
                  <rect x="84" y={m.y + r * 8} width="16" height="2.6" fill="var(--ink-3)" rx="0.5" />
                </g>
              ))}
            </g>
          ) : m.t === "chart" ? (
            <g key={i}>
              <polyline
                points={`0,${m.y + m.h} 14,${m.y + m.h * 0.72} 28,${m.y + m.h * 0.8} 42,${m.y + m.h * 0.44} 56,${m.y + m.h * 0.52} 70,${m.y + m.h * 0.24} 84,${m.y + m.h * 0.3} 100,${m.y + m.h * 0.08}`}
                fill="none"
                stroke="var(--red)"
                strokeWidth="1.4"
              />
              <line x1="0" y1={m.y + m.h} x2="100" y2={m.y + m.h} stroke="var(--paper-3)" strokeWidth="0.7" />
            </g>
          ) : (
            <g key={i}>
              <circle cx="50" cy={m.y + 8} r="8" fill="none" stroke="var(--red)" strokeWidth="1.2" />
              <circle cx="50" cy={m.y + 8} r="4.4" fill="none" stroke="var(--paper-3)" strokeWidth="1" />
            </g>
          )
        )}
      </svg>
    </div>
  );
}
